<template>
	<ValidationProvider :rules="rules" v-slot="{ errors }">
		<v-autocomplete
			:value="value"
			:items="items"
			item-text="name"
			item-value="id"
			hide-no-data
			hide-details
			outlined
			:label="label"
			@change="handleChange"
			:multiple="multiple"
			:chips="multiple"
			:deletable-chips="multiple"
			:clearable="clearable"
			class="v-select-talentics-chips job-picker-autocomplete"
			:error-messages="errors"
			:dense="dense"
			:disabled="disabled"
			:menu-props="{ maxHeight: 420, contentClass: 'job-picker-menu' }"
		>
			<template v-slot:item="{ item, attrs, on }">
				<v-list-item
					v-if="item.header"
					:key="'h-' + item.header"
					disabled
					class="job-picker-section-header"
				>
					<v-list-item-content class="py-1">
						<v-list-item-subtitle
							class="text-caption font-weight-medium text-uppercase"
							style="opacity: 0.75"
						>
							{{ item.header }}
						</v-list-item-subtitle>
					</v-list-item-content>
				</v-list-item>
				<v-list-item
					v-else
					v-bind="attrs"
					v-on="on"
					:key="'j-' + item.id"
					class="job-picker-job-row"
					two-line
				>
					<v-list-item-content>
						<v-list-item-title class="job-picker-job-title">
							{{ item.rawName }} ({{ item.identifier }})
							<v-tooltip
								v-if="item.list_on_external && item.status === 'open'"
								bottom
							>
								<template v-slot:activator="{ on: tooltipOn }">
									<v-icon
										small
										color="primary"
										class="ml-1"
										style="font-size: 16px"
										v-on="tooltipOn"
									>
										public
									</v-icon>
								</template>
								<span>{{ $t('tooltip.listed-on-career-page') }}</span>
							</v-tooltip>
						</v-list-item-title>
						<v-list-item-subtitle
							v-if="item.status || getJobMetadata(item)"
							class="text-caption mt-1 job-picker-job-subtitle"
						>
							<span v-if="item.status" class="job-picker-status-wrap">
								<v-chip
									x-small
									:color="getJobStatusColor(item.status)"
									:dark="isStatusDark(item.status)"
									class="job-picker-status-chip"
								>
									{{ $t(`status.${item.status}`) }}
								</v-chip>
							</span>
							<span v-if="getJobMetadata(item)">{{ getJobMetadata(item) }}</span>
						</v-list-item-subtitle>
					</v-list-item-content>
				</v-list-item>
			</template>
			<template v-slot:prepend-item>
				<div>
					<div v-if="hideClosedJobs !== true">
						<div style="padding-left: 1rem; padding-bottom: 0.5rem">
							<v-switch
								v-model="listClosedJobs"
								color="success"
								class="v-input--template-switch"
								hide-details
								inset
								dense
								:label="$t('label.show-closed-jobs')"
							></v-switch>
						</div>
						<v-divider></v-divider>
					</div>

					<div v-if="hideArchivedJobs !== true">
						<div
							style="
								padding-left: 1rem;
								padding-bottom: 0.5rem;
								padding-top: 0.5rem;
							"
						>
							<v-switch
								v-model="listArchivedJobs"
								color="success"
								class="v-input--template-switch"
								hide-details
								inset
								dense
								:label="$t('label.show-archived-jobs')"
							></v-switch>
						</div>
						<v-divider></v-divider>
					</div>

					<div v-if="hideDraftJobs !== true">
						<div
							style="
								padding-left: 1rem;
								padding-bottom: 0.5rem;
								padding-top: 0.5rem;
							"
						>
							<v-switch
								v-model="listDraftJobs"
								color="success"
								class="v-input--template-switch"
								hide-details
								inset
								dense
								:label="$t('label.show-draft-jobs')"
							></v-switch>
						</div>
						<v-divider></v-divider>
					</div>

					<div v-if="hidePendingJobs !== true">
						<div
							style="
								padding-left: 1rem;
								padding-bottom: 0.5rem;
								padding-top: 0.5rem;
							"
						>
							<v-switch
								v-model="listPendingJobs"
								color="success"
								class="v-input--template-switch"
								hide-details
								inset
								dense
								:label="$t('label.show-pending-jobs')"
							></v-switch>
						</div>
						<v-divider></v-divider>
					</div>
				</div>
			</template>
		</v-autocomplete>
	</ValidationProvider>
</template>

<script>
import { mapState } from 'vuex';
import { ValidationProvider } from 'vee-validate';

export default {
	name: 'JobPicker',
	props: {
		jobs: {
			type: Array,
			required: false,
			default() {
				return [];
			},
		},
		value: {
			type: [Number, Array],
			required: false,
			default() {
				return null;
			},
		},
		label: {
			type: String,
			required: false,
			default() {
				return '';
			},
		},
		multiple: {
			type: Boolean,
			required: false,
			default() {
				return false;
			},
		},
		clearable: {
			type: Boolean,
			required: false,
			default() {
				return true;
			},
		},
		rules: {
			type: String,
			required: false,
			default() {
				return '';
			},
		},
		hideInactiveTeams: {
			type: Boolean,
			required: false,
		},
		hideInactiveDepartments: {
			type: Boolean,
			required: false,
		},
		hideDraftJobs: {
			type: Boolean,
			required: false,
		},
		hideClosedJobs: {
			type: Boolean,
			required: false,
		},
		hideArchivedJobs: {
			type: Boolean,
			required: false,
		},
		hidePendingJobs: {
			type: Boolean,
			required: false,
			default: false,
		},
		dense: {
			type: Boolean,
			required: false,
			default() {
				return false;
			},
		},
		disabled: {
			type: Boolean,
			required: false,
			default() {
				return false;
			},
		},
	},
	components: {
		ValidationProvider,
	},
	computed: {
		...mapState({
			departments: state => state.departments.items,
			teams: state => state.teams.items,
			currentLang: state => state.currentUser.lang,
		}),
		items() {
			const {
				departments,
				jobs,
				teams,
				hideInactiveDepartments,
				hideInactiveTeams,
				listClosedJobs,
				listArchivedJobs,
				listDraftJobs,
				listPendingJobs,
			} = this;

			const filterDepartments = department => {
				if (
					listClosedJobs ||
					listArchivedJobs ||
					listDraftJobs ||
					listPendingJobs
				) {
					return true;
				}
				return hideInactiveDepartments ? department.active : true;
			};

			const filterTeams = team => {
				if (
					listClosedJobs ||
					listArchivedJobs ||
					listDraftJobs ||
					listPendingJobs
				) {
					return true;
				}
				return hideInactiveTeams ? team.active : true;
			};

			const deptTeams = departments
				.filter(filterDepartments)
				.reduce((acc, department) => {
					const matchingTeams = teams
						.filter(filterTeams)
						.filter(({ department_id }) => department_id === department.id)
						.map(team => ({
							header: `${department.name} - ${team.name}`,
							team_id: team.id,
						}));
					return [...acc, ...matchingTeams];
				}, []);

			const processedJobs = deptTeams.reduce((acc, deptTeam) => {
				const deptTeamJobs = jobs
					.filter(({ team_id }) => team_id === deptTeam.team_id)
					.map(job => ({
						...job,
						rawName: job.name,
						name: `${job.name} (${job.identifier})`,
					}));

				return [
					...acc,
					...(deptTeamJobs.length > 0 ? [deptTeam] : []),
					...deptTeamJobs,
				];
			}, []);

			const isHeader = item => item != null && 'header' in item;
			return processedJobs
				.filter(
					item =>
						isHeader(item) ||
						(listClosedJobs ? true : item.status !== 'closed'),
				)
				.filter(
					item =>
						isHeader(item) ||
						(listArchivedJobs ? true : item.status !== 'archived'),
				)
				.filter(
					item =>
						isHeader(item) ||
						(listPendingJobs ? true : item.status !== 'pending'),
				)
				.filter(
					item =>
						isHeader(item) ||
						(listDraftJobs ? true : item.status !== 'draft'),
				)
		},
	},
	data() {
		return {
			listClosedJobs: false,
			listArchivedJobs: false,
			listDraftJobs: false,
			listPendingJobs: false,
		};
	},
	methods: {
		handleChange(newVal) {
			this.$emit('input', newVal);
		},
		getJobStatusColor(status) {
			const statusColors = {
				open: 'success',
				closed: 'error',
				draft: 'warning',
				pending: 'info',
				archived: 'grey darken-2',
			};
			return statusColors[status] || 'grey';
		},
		isStatusDark(status) {
			return ['open', 'closed', 'archived', 'pending'].includes(status);
		},
		getJobMetadata(job) {
			if (!job || job.header) {
				return null;
			}
			const { teams, departments, currentLang } = this;
			const team = teams.find(t => t.id === job.team_id);
			const additionalInfo = [];
			if (team) {
				additionalInfo.push(team.name);
				const department = departments.find(d => d.id === team.department_id);
				if (department) {
					additionalInfo.push(department.name);
				}
			}
			if (job.created) {
				const createdDate = new Date(job.created);
				const formattedDate = createdDate.toLocaleDateString(
					currentLang === 'en' ? 'en-US' : currentLang,
					{ year: 'numeric', month: 'short', day: 'numeric' },
				);
				additionalInfo.push(`${this.$t('label.created_at')}: ${formattedDate}`);
			}
			return additionalInfo.length > 0 ? additionalInfo.join(' · ') : null;
		},
	},
};
</script>

<style lang="scss" scoped>
.job-picker-job-row {
	min-height: 56px !important;
	align-items: flex-start;
}

.job-picker-job-title {
	font-weight: 600;
	line-height: 1.35;
	white-space: normal;
	word-break: break-word;
}

.job-picker-job-subtitle {
	opacity: 0.7;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 4px 8px;
}

.job-picker-status-chip {
	height: 18px !important;
	font-size: 11px !important;
}

.job-picker-section-header {
	min-height: 36px !important;
}
</style>
